import { XCircleIcon } from '@heroicons/react/24/outline';
import React, { Component, ErrorInfo, ReactNode } from 'react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * IntegrationErrorBoundary prevents complete app crashes from integration modal errors.
 *
 * @remarks
 * Wraps integration modals to catch render errors and display fallback UI
 * instead of crashing the entire application.
 *
 * Features:
 * - Catches React errors in child components
 * - Displays user-friendly error modal
 * - Allows graceful recovery via "Try Again" button
 * - Logs errors for debugging
 */
export class IntegrationErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Integration Modal Error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Modal
          open={true}
          onClose={this.handleReset}
          size="lg"
          title="Integration Error"
        >
          <div className="flex flex-col items-center p-8 text-center">
            <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              The integration modal encountered an unexpected error. Please try
              again or contact support if the issue persists.
            </p>
            <Button
              onClick={this.handleReset}
              className="mt-6"
              styleType="primary"
            >
              Try Again
            </Button>
          </div>
        </Modal>
      );
    }

    return this.props.children;
  }
}
